/******************************************************************************
File: cgp-grid.component.ts
Author: Jared Craig - jared.craig@gm.com
Date: 10-10-2017
Description: Generic, reusable interface for kendo grid.
Other contributors: Neel Radia
******************************************************************************/

import {
    Component,
    OnInit,
    Input,
    ViewChild,
    OnDestroy,
    AfterViewChecked,
    AfterViewInit,
    Renderer2,
    ChangeDetectorRef,
    HostListener
} from '@angular/core';
import { process } from '@progress/kendo-data-query';
import { GridDataResult, FilterService, DataStateChangeEvent, GridComponent, RowClassArgs } from '@progress/kendo-angular-grid';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';

// CGP Dependencies
import { CgpConfirmationDialogService } from '../cgp-confirmation-dialog/cgp-confirmation-dialog.service';
import { CgpSnackbarService } from '../cgp-snackbar/cgp-snackbar.service';
import { CgpAlertDialogService } from '../cgp-alert-dialog/cgp-alert-dialog.service';
import { DataGridTemplate, flatten, matches } from './data-grid.template';
import { messages, exceptions, exceptionTypes } from './data-grid.config';
import { Subject } from 'rxjs';
import { ExcelExportData } from '@progress/kendo-angular-excel-export';
import { ColumnTemplatePipe } from './data-grid.pipe';
import { UntypedFormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { UploadEvent } from '@progress/kendo-angular-upload';
import { UserSettingsService } from '../../components/ve/ve.service';
import { AuthenticationService } from '../../auth.service';
import { takeUntil } from 'rxjs/operators';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

@Component({
    // tslint:disable-next-line:component-selector
    selector: 'data-grid',
    templateUrl: './data-grid.component.html',
    styleUrls: ['./data-grid.component.scss'],
})
export class DataGridComponent implements OnInit, OnDestroy, AfterViewChecked, AfterViewInit {
    /**
     *
     * @param cgpConfirmationDialogService
     * @param cgpSnackbarService
     * @param cgpAlertDialogService
     */
    constructor(
        private readonly cgpConfirmationDialogService: CgpConfirmationDialogService,
        private readonly cgpSnackbarService: CgpSnackbarService,
        private readonly cgpAlertDialogService: CgpAlertDialogService,
        private readonly formBuilder: UntypedFormBuilder,
        private readonly router: Router,
        private readonly ref: ChangeDetectorRef,
        private readonly userSettingsService: UserSettingsService,
        private readonly authService: AuthenticationService,
        private readonly renderer: Renderer2) {
    }
    /**
     *
     */
    // @HostBinding('class') gridClass = 'flex-stretch';
    /**
     *
     */
    @ViewChild('grid', { static: false }) grid: GridComponent; 
    /**
     *
     */
    @ViewChild(TooltipDirective, { static: true }) tooltipDir: TooltipDirective;
    /**
     *
     */
    filterForm = this.formBuilder.group({ filter: [] });
    /**
     * Configuration dialog visibility
     */
    isShowConfig: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    /**
     *
     */
    protected gridData: Observable<GridDataResult>;
    /**
     *
     */
    private readonly ngUnsubscribe: Subject<void> = new Subject<void>();
    /**
     *
     */
    @Input() template: DataGridTemplate;
    /**
     *
     */
    @Input() parentEditDataItem: any;
    /**
     *
     */
    private exportDate: string;
    /**
     *
     */
    private docClickSubscription: any;
    /**
     *
     */

    ngAfterViewInit(): void {
        //this is kept empty on purpose
    }

    ngOnInit() {
        if (this.template) {
            this.template.httpResponse
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe(x => {
                    if (x) { this.handleResponse(x.response, x.message); }
                });
        }


        this.template.changed
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => {
                this.refresh(true);
            });

        this.filterForm.get('filter').valueChanges
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => this.handleFilterChange);

        this.docClickSubscription = this.renderer
            .listen('document', 'click', this.onDocumentClick.bind(this));

        this.authService.getUserProfile()
            .subscribe(result => {
                if (result.GMIN && this.template.controls.configure) {
                    this.setHiddenColumns();
                }
            });

    }


    /**
     *
     */
    ngOnDestroy() {
        this.docClickSubscription();
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }


    /**
     *
     */
    ngAfterViewChecked(): void { 
        //this is kept empty on purpose
    }


    /**
     *
     * @param state
     */
    onStateChange(state: DataStateChangeEvent): void {
        if (this.template.gridState.take !== state.take) {
            this.template.gridState = state;
        }
        this.template.gridState = state;
        const _dataResult = process(this.template.data, this.template.gridState);
        this.gridData = of(_dataResult);
        this.refresh(false);
    }


    /**
     *
     * @param forceRefresh
     */
    refresh(forceRefresh: boolean) {
        this.template.isLoading.next(true);
        const _options = {
            parentEditDataItem: this.parentEditDataItem,
            force: forceRefresh
        };
        this.template.read(_options)
            .then(response => {
                this.template.isLoading.next(false);
                this.setGridData(response);
                this.template.isSelectAll = false;
                this.template.selectedRows = [];
            })
            .catch(e => this.handleError(e, exceptions.gridRead));
    }


    /**
     *
     *
     * @memberof DataGridComponent
     */
    setHiddenColumns = () => {
        const userGmin = this.authService.getUserGMIN();
        this.userSettingsService.read(`/user/${userGmin}/${this.authService.getModule()}`, false)
            .then(results => {
                if (results) {
                    const visibleColumns = results.Settings.split(',');
                    this.grid.columns.forEach(column => {
                        if (!visibleColumns.find(x => x === column.title)) {
                            column.hidden = true;
                        }
                    });
                }
            })
            .catch(exception => {
                console.log(exception);
            });
    }


    /**
     *
     */
    getColumnValue = (column: any, dataItem: any): string => {
        const _field: string = column.detail ? column.detail.field : column.field;
        const _obj: any = dataItem[_field];
        let response = _obj;

        if (!_obj) {
            response = dataItem;
            column.field
                .split('.')
                .forEach(f => response ? response = response[f] : null);
        }

        if (_obj && column.detail) {
            response = _obj[column.detail.textField];
        } else if (column.template && column.template !== 'date') {
            response = new ColumnTemplatePipe()
                .transform(column, dataItem);
        }
        return response === null ? '' : response;
    }


    /**
     *
     */
    getMultiSelectFilterValue = (column, filter): string[] => {
        const selectedFilter = flatten(filter).map(({ value }) => value.toString());
        return selectedFilter.length > 0 ? selectedFilter : null;
    }


    /**
     *
     */
    getFilterValue = (column, filter): string[] | boolean => {
        const _value = filter.filters.value;
        if (column.template && column.template.type === 'boolean') { return _value; }
        return _value ? [_value] : this.getMultiSelectFilterValue(column, filter);
    }


    /**
     *
     */
    getComboboxFilterValue = (column, filter): any => {
        const _filter = filter.filters.find(() => true);
        return _filter ? _filter.value : _filter;
    }


    /**
     *
     */
    handleFilterChange = (value: string, column: any, filterService: FilterService) => {
        const operator = column.template.operator || 'contains';
        filterService.filter({
            filters: [{
                field: column.field,
                operator,
                value
            }],
            logic: 'or'
        });
    }


    /**
     *
     */
    handleMultiselectFilterChange = (values: any[], column: any, filterService: FilterService) => {
        const operator = column.template.operator || 'contains';
        filterService.filter({
            filters: values.map(value => ({
                field: column.field,
                operator,
                value
            })),
            logic: 'or'
        });
    }

    /**
     *
     */
    filterSelectAll = (column: any, filterService: FilterService) => {
        if (column && column.template && column.template.service) {
            const selection = column.template.service.value;
            this.handleMultiselectFilterChange(selection, column, filterService);
        }
    }

    /**
     * Inline grid (cancel) event handler
     */
    cancelHandler = ({ sender, rowIndex = this.template.editedRowIndex }) => {
        if (sender) { this.closeEditor(sender, rowIndex); }
        this.template.isEditing.next(false);
    }


    /**
     * Grid (add) event handler
     * Issue template's (onCreate) callback
     * If editing inline, open new row for editing to top of grid
     */
    addHandler = ({ sender, dataItem = {} }) => {
        this.template.onCreate(dataItem);

        if (!this.template.modal) {
            this.closeEditor(sender);
            if (this.template.editForm) {
                sender.addRow(this.template.editForm);
                this.template.isEditing.next(true);
            } else {
                this.handleError(exceptions.editForm, exceptionTypes.undefined);
            }
        }
    }


    editClick = (e) => {
        if (!this.template.isClickEdit) { return; }
        const isClickOffRow = e.rowIndex >= 0 && this.template.editedRowIndex !== e.rowIndex;
        if (isClickOffRow) {
            this.template.sender = e.sender;
            this.editHandler(e);
        }
    }

    private onDocumentClick(e: any): void {
        const form = this.template.editForm;
        if (this.template.isEditing.value &&
            !matches(e.target, '#grid tbody *, #grid .k-grid-toolbar .k-button, .btn, .k-checkbox, .k-upload-status *')) {
            if (form.valid) {
                this.saveCurrent();
            } else {
                Object.keys(form.controls)
                    .forEach(key => {
                        form.get(key).markAsTouched();
                    });
            }
        }
    }

    private saveCurrent(): void {
        const isEditingDetail = this.template.detailTemplate && this.template.detailTemplate.isEditing.value;

        const template = isEditingDetail ? this.template.detailTemplate : this.template;

        this.closeEditor(template.sender || this.grid, template.editedRowIndex);
        template.isEditing.next(false);

        if (!template.editForm.pristine) {
            Object.keys(template.editForm.controls).forEach(k => template.editForm.get(k).enable());
            template.onSave({ dataItem: template.editForm.value, isNew: false });
        } else {
            template.editedRowIndex = undefined;
            template.editDataItem = undefined;
        }
    }

    @HostListener('document:keyup', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent) {
        if (event.keyCode === 13 && this.template.isClickEdit && this.template.isEditing.value && this.template.isSubmitOnEnter) {
            this.saveCurrent();
        }
        event.preventDefault();
    }

    /**
     * Grid (edit) event handler
     * Issue template's (onCreate) callback
     * If editing inline, open selected grid row for editing
     */
    editHandler = ({ sender, rowIndex, dataItem }) => {
        const isEditingDetail = this.template.detailTemplate && this.template.detailTemplate.isEditing.value;

        const _lastRow = this.template.editedRowIndex;

        if (this.template.isClickEdit &&
            (this.template.isEditing.value || isEditingDetail) &&
            (this.template.editForm.valid || isEditingDetail && this.template.detailTemplate.editForm.valid)
        ) {
            this.saveCurrent();
        }

        if (!this.template.modal) {
            if (!this.template.canEdit(dataItem)) { return; }
            this.closeEditor(sender, _lastRow);
            sender.editRow(rowIndex, this.template.editForm);
            this.template.isEditing.next(true);
        }
        this.template.onEdit(dataItem, rowIndex);
    }


    /**
     * Grid (save) event handler
     * Issue template's (onSave) callback
     */
    saveHandler = ({ sender, rowIndex, formGroup, isNew }) => {
        this.template.onSave({ dataItem: formGroup.value, isNew });
        this.closeEditor(sender, rowIndex);
        this.template.isEditing.next(false);
    }


    /**
     * Grid (remove) event handler
     * Prompt user with confirmation dialog
     * YES: Issue template's (delete) callback and close dialog
     * NO: Close dialog
     */
    removeHandler = ({ dataItem }) => {
        const source: string = this.template.getSource(dataItem);
        const customMessage = this.template.getDeleteMessage(dataItem);
        this.cgpConfirmationDialogService.showConfirmationDialog({
            title: 'Delete Confirmation',
            message: customMessage ? customMessage : `${messages.recordDeleteConfirm}${source}?`,
            alert: 'danger',
            onYesClick: () => {
                const response = this.template.delete(dataItem);
                this.handleResponse(response, source + messages.recordDeleteSuccess);
                this.cgpConfirmationDialogService.closeConfirmationDialog();
            },
            onNoClick: () => {
                this.cgpConfirmationDialogService.closeConfirmationDialog();
            }
        });
    }


    /**
     *
     */
    handleToolBarButtonClick = (button, grid) => {
        button.callback(grid);
        if (button.refresh) { this.refresh(true); }
    }


    /**
     *
     */
    handleCommandButtonClick = (button, dataItem) => {
        button.callback(button, dataItem);
        if (button.refresh) { this.refresh(true); }
    }


    /**
     *
     */
    onSelectionChange = (e) => {
        if (this.template.selectableSettings.mode === 'single') {
            this.template.selectedRows = e.selectedRows;
        } else {
            if (e.selectedRows.length > 0) {
                this.template.selectedRows.push(e.selectedRows[0]);
            } else {
                this.template.sender = this.grid;
                const index = this.template.selectedRows.findIndex(x => x.dataItem === e.deselectedRows[0].dataItem);
                this.template.selectedRows.splice(index, 1);
            }
        }

        if (this.template.editOnselect) {
            const dataItem = e.selectedRows.length > 0 ? e.selectedRows[0].dataItem : e.deselectedRows[0].dataItem;
            const index = e.selectedRows.length > 0 ? e.selectedRows[0].index : e.deselectedRows[0].index;
            const opts = { sender: this.grid, rowIndex: index, dataItem };
            this.editHandler(opts);
        }

        this.template.select(e.selectedRows[0]);
    }

    onColumnVisibilityChange = (e) => {
        const visibleColumns = [];
        this.grid.columns.map(column => {
            if (!column.hidden) {
                visibleColumns.push(column.title);
            }
        });
        const dataObject = {
            user: this.authService.getUserGMIN(),
            ModuleId: EMPTY_GUID,
            settings: visibleColumns.toString(),
        };
        this.userSettingsService.save(dataObject, true, `/upsert?module=${this.authService.getModule()}`);
        console.log(dataObject);
    }


    /**
     *
     */
    isRowSelected = (e) => {
        return this.template.selectedRows.some(x => x.index === e.index);
    }


    /**
     *
     */
    isShowCommandColumn = (): boolean => {
        try {
            const ctrl = this.template.controls;
            return ctrl && (ctrl.edit || ctrl.delete || (ctrl.customButtons && ctrl.customButtons.length > 0));
        } catch (e) {
            return true;
        }
    }


    /**
     *
     */
    isFilterTemplate = (template: any): boolean => {
        return template && (
            template.type === 'boolean' ||
            template.type === 'multiselect' || template.filterType === 'multiselect' ||
            template.type === 'combobox' || template.filterType === 'combobox' ||
            template.type === 'dropdown' ||
            template.type === 'form'
        );
    }


    /**
     *
     * @param column
     * @param type
     */
    isFilterMatch(column: any, type: string): boolean {
        if (column && column.template) {
            if (column.template.filterType) {
                return column.template.filterType === type;
            }
            return column.template.type === type;
        }
        return false;
    }


    /**
     *
     * @param columnField
     */
    isGroupingField(columnField: string) {
        const test = this.template.gridState.group.length > 0;
        const test2 = columnField === this.template.gridState.group[0].field;
        return test && test2;
    }


    /**
     *
     */
    toggleSelectAll = (e) => {
        this.template.isSelectAll = e.target.checked;
        this.template.selectedRows = this.template.isSelectAll ?
            this.template.data.map((item, i) => {
                return {
                    dataItem: item,
                    index: i + this.template.gridState.skip
                };
            }) : [];
        this.template.select(this.template.selectedRows);
    }


    /**
     *
     */
    getColumnWidth = (column) => {
        return column.field && !column.width ? null : column.width;
    }


    /**
     *
     */
    getControlColumnWidth = () => {
        let baseWidth = 24;
        const ctrl = this.template.controls;
        if (ctrl.width) { return ctrl.width; }
        if (ctrl.edit) { baseWidth += 45; }
        if (ctrl.delete) { baseWidth += 65; }
        if (ctrl.customButtons) { baseWidth += (ctrl.customButtons.length * 60); }
        if (!this.template.modal && !ctrl.delete) { baseWidth += 80; }
        return baseWidth;
    }


    /**
     *
     */
    getDetailRowHeader = (dataItem): string => {
        try {
            const dt = this.template.detailTemplate,
                field = dt.detailTitleField ?
                    (dataItem[dt.detailTitleField])
                    : '';
            return `${dt.title}${field ? ' - ' : ''}${field}`;
        } catch (e) {
            return '';
        }
    }


    /**
     *
     */
    private getDatePath(): string {
        if (!this.exportDate) {
            const url: any[] = this.router.url.split('/');
            if (url.length > 0 && url[0] === '') { url.shift(); }
            const date = new Date();
            this.exportDate = url.concat([date.getMonth() + 1, date.getDate(), date.getFullYear()])
                .join('_');
        }
        return this.exportDate;
    }


    /**
     *
     */
    getExportFilename = (extension): string => {
        const _fileName = this.getDatePath() + extension;

        try {
            const fileName: string = this.template.fileName ?
                this.template.fileName : _fileName;
            return fileName.toLowerCase().endsWith(extension) ?
                fileName : fileName + extension;
        } catch (e) {
            return `grid.${extension}`;
        }
    }

    /**
     *
     */
    isSelectAllDisabled = (): boolean => {
        return !this.template.data || this.template.data.length < 1;
    }


    /**
     * Show configuration dialog
     */
    showConfigDialog = () => this.isShowConfig.next(true);


    /**
     * Shows the action button. Filtered by reports, editing, and deleting work orders.
     * Report button is rendered to everyone if the work order is not draft, unassigned, or cancelled.
     * Edit button is rendered to everyone unless a work is assigned or in progress, which users are no longer
     * allowed to edit or it is completed or cancelled.
     *
     * @memberof DataGridComponent
     */
    isShowButton = (button, dataItem): boolean => {
        const user = this.authService.getUser();
        if (button.id === 'report') {
            return this.isReportable(dataItem, user);
        } else if (button.id === 'edit') {
            return this.isEditable(dataItem, user);
        } else if (button.id === 'delete') {
            return this.isDeletable(dataItem, user);
        } else {
            return button.isShow != null ? button.isShow : true;
        }
    }

    /**
     * Function determines if a workorder can be edited. Criteria for editing is if the user is a VE_User and
     * the workorder isn't ongoing/inProgress, the VE_User can edit. All other roles can edit at any point.
     *
     * @param {*} dataItem
     * @returns
     * @memberof DataGridComponent
     */
    isEditable(dataItem, user) {
        if (dataItem.Status === 'InProgress' || dataItem.Status === 'OnHold') {
            const isUser = this.isUserInRole(user, 'User');
            const numberOfUserRoles = this.getNumberofUserRolesForModule(user);
            if (isUser && numberOfUserRoles === 1) {
                return false;
            }
        } else if (dataItem.Status === 'Complete' || dataItem.Status === 'Cancelled') {
            return false;
        }
        return this.isUserInRole(user, 'Administrator') || this.isUserInRole(user, 'Manager') ||
            this.isUserInRole(user, 'Engineer') || this.isUserInRole(user, 'Lead') ?
            true : this.isCreatedBy(dataItem, user);
    }

    /**
     * Determines if the user can provide a report for the workorder.
     *
     * @param {*} dataItem
     * @param {*} user
     * @returns
     * @memberof DataGridComponent
     */
    isReportable(dataItem, user) {

        if (dataItem.Status !== 'Draft' && dataItem.Status !== 'Cancelled' && dataItem.Status !== 'UnAssigned') {
            const isUser = this.isUserInRole(user, 'User');
            const isManager = this.isUserInRole(user, 'Manager');
            const numberOfUserRoles = this.getNumberofUserRolesForModule(user);

            if ((isUser && numberOfUserRoles === 1) || (isManager && numberOfUserRoles === 1) || isManager && isUser) {
                return false;
            } else {
                return true;
            }
        } else {
            return false;
        }
    }

    /**
     * Determines if the workorder was created by the current user.
     *
     * @param {*} dataItem
     * @param {*} user
     * @returns
     * @memberof DataGridComponent
     */
    isCreatedBy(dataItem, user) {
        return dataItem.CreatedBy.toString() === user.GMIN;
    }

    /**
     * Function determines if a workorder can be deleted. Criteria for deleting is if the user is a VE_User and
     * the workorder isn't ongoing/inProgress, the VE_User can delete. All other roles can delete at any point.
     *
     * @param {*} dataItem
     * @returns
     * @memberof DataGridComponent
     */
    isDeletable(dataItem, user) {
        if (dataItem.Status !== 'Draft') {
            const isAdmin = this.isUserInRole(user, 'Admin');
            const isLead = this.isUserInRole(user, 'Lead');
            if (!isLead && !isAdmin) {
                return false;
            }
        }
        return true;
    }

    /**
     * Determines if the user is only a VE_User.
     *
     * @param {*} user
     * @param {*} role
     * @returns
     * @memberof DataGridComponent
     */
    isUserInRole(user, role) {
        const isVERole = user.RoleClaims.filter(roleClaim => {
            const userRoleClaimsContents = roleClaim.split('_');
            const userRoleClaimsModule = userRoleClaimsContents[0];
            const userRoleClaimsRole = userRoleClaimsContents[2];
            const currentUserModule = this.authService.getModule().split('_');
            if (userRoleClaimsRole && userRoleClaimsRole.includes(role) && currentUserModule[1].toLowerCase() === userRoleClaimsModule.toLowerCase()) {
                return roleClaim;
            }
        });
        return isVERole.length !== 0;
    }

    /**
     * Returns the number of role claims for a user in the current module.
     *
     * @param {*} user
     * @returns
     * @memberof DataGridComponent
     */
    getNumberofUserRolesForModule(user) {
        const userModuleRoles = user.RoleClaims.filter(roleClaim => {
            const userRoleClaimsContents = roleClaim.split('_');
            const userRoleClaimsModule = userRoleClaimsContents[0];
            const currentUserModule = this.authService.getModule().split('_');
            if (currentUserModule[1].toLowerCase() === userRoleClaimsModule.toLowerCase()) {
                return roleClaim;
            }
        });
        return userModuleRoles.length;
    }


    /**
     *
     */
    isShowToolbar = (): boolean => {
        const tc = this.template.controls;
        return tc.add || tc.excel || tc.customToolBarButtons.length > 0;
    }


    /**
     * Excel Export - Exports all columns with applied filters
     */
    allData = (): ExcelExportData => {
        let data = this.template.data;
        if (!this.template.fileName) { this.template.fileName = this.template.title; }
        if (data.length > 0 && data[0] === undefined) { data = []; }

        return {
            data: process(data, {
                group: this.template.gridState.group,
                sort: this.template.gridState.sort,
                filter: this.template.gridState.filter
            }).data,
            group: this.template.gridState.group
        };
    }


    /**
     *
     */
    isHidden = (isVisible: any): boolean => typeof (isVisible) === typeof (true) ? !isVisible : false;


    /**
     *
     * @param response
     */
    private getResponseData(response): any[] {
        if (response && response.total) {
            this.template.recordCount = response.total;
        }
        return response && response.data && response.total ?
            response.data :
            response && response.length && response[0] ?
                response :
                [];
    }


    /**
     *
     * @param response
     */
    private setGridData(response) {
        this.template.data = this.getResponseData(response);
        const _dataResult: GridDataResult =
            this.template.isAll ?
                this.processAllData() :
                this.template.isServerSideFiltering ?
                    process(this.template.data, {
                        skip: this.template.gridState.skip,
                        group: this.template.gridState.group
                    }) :
                    process(this.template.data, this.template.gridState);
        if (response && response.total !== undefined) {
            _dataResult.total = response.total;
        }
        this.gridData = of(_dataResult);
        this.gridData
            .subscribe(x => {
                if (x.data.length && this.template.collapseOnGroup && x.data[0].aggregates) {
                    this.collapseGroups(x.data);
                }
            });
    }


    /**
     *
     */
    private processAllData(): GridDataResult {
        this.template.gridState.take = this.template.data.length;
        this.template.gridState.skip = 0;
        return process(this.template.data, this.template.gridState);
    }


    /**
     *
     * @param grid
     * @param rowIndex
     */
    private closeEditor(sender: any, rowIndex = this.template.editedRowIndex) {
        sender.closeRow(rowIndex);
    }


    /**
     *
     * @param response
     * @param messageText
     */
    private handleResponse(response: Promise<any>, messageText: string) {
        if (!response) { return; }
        response.then(() => {
            this.refresh(true);

            if (this.template.isNotificationsEnabled) {
                this.cgpSnackbarService.showSnackbar({
                    message: messageText,
                    duration: 2000
                });
            }
        })
            .catch(e => {
                this.refresh(true);
                this.handleError(e, `Http Error: ${e.status}`);
            });
    }


    /**
     *
     * @param message
     * @param title
     */
    private handleError(e: any, title = 'Error') {
        this.template.isLoading.next(false);
        const _body = e.error ? e.error : e;
        this.cgpAlertDialogService.showAlertDialog({
            title,
            message: _body,
            alert: 'danger',
            closeLabel: 'OK',
        });
    }

    isAutoLock = (): boolean => {
        return this.template.columns.filter(c => c['visible']).length > 8;
    }

    public showTooltip(e: any): void {
        if (e.target.offsetWidth < e.target.scrollWidth) {
            this.tooltipDir.toggle(e.target);
        } else {
            this.tooltipDir.hide();
        }
    }

    isLoading(): boolean {
        return !this.parentEditDataItem &&
            this.template && this.template.isLoading.value;
    }


    private collapseGroups(data) {
        data.forEach((x, i) => this.grid.collapseGroup((i).toString()));
    }

    trackByCallback(index, item) {
        if (!item) { return null; }
        return item.Id || item.id || item;
    }

    selectFiles = (e: UploadEvent, column: any) => {
        e.preventDefault();
        const ctrl = this.template.editForm.get(column.template.name || column.field);
        if (ctrl) {
            const fileList = ctrl.value || [];
            e.files.forEach(f => {
                if (!fileList.some(x => x.name === f.name)) {
                    fileList.push(f);
                }
            });
            ctrl.setValue(fileList);
        }

        if (column.template.select) {
            column.template.select(e);
        }
    }

    removeFile = (file, column) => {
        const ctrl = this.template.editForm.get(column.template.name || column.field);
        if (ctrl) {
            ctrl.setValue(ctrl.value.filter(f => f.uid !== file.uid));
        }
    }

    handleButtonClick(e, column) {
        column.template.handleButtonClick(e);
    }

    /**
     *
     * @param context
     * Used to set alternating row colors in grid
    */
    public rowCallback(context: RowClassArgs) {
        const isEven = context.index % 2 === 0;
        return {
            even: isEven,
            odd: !isEven
        };
    }
} // End CGP Grid
